import React, {
  useState, createRef, useEffect, useRef,
} from "react";
import dayjs from "dayjs";
import Spinner from "react-bootstrap/Spinner";
import {
  Stack,
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import {
  CardComponent,
  CardNumber,
  CardExpiry,
  CardCVV,
} from "@chargebee/chargebee-js-react-wrapper"; // @ts-ignore
import { useForm } from "react-hook-form";
import { Check, CheckCircleFill, CheckLg } from "react-bootstrap-icons";
import DiscountModal from "./DiscountModal";
import Countdown from "./Cowntdown";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectSignUp,
  setCbToken,
  setCoupon,
  setMarketingTerms,
} from "../features/signup/signupSlice";
import { pineAppleDay, pineAppleTime } from "../lib/utils";
import {
  selectDiscount,
  newCampaignDiscount,
  setDiscount,
  discountFromCoupon,
  discountWording,
} from "../features/discount/discountSlice";
import OrderSummary, { getDiscountPlanCost } from "./OrderSummary";
import {
  ValidateCoupon,
  CheckCoupon,
  Coupon,
  CouponType,
} from "../features/coupon/couponAPI";
import { postBookingsAsync, postOrderAsync } from "../features/finale/finaleSlice";
import { isCommunityFibrePlan } from "../features/finale/orderAPI";
import {
  selectWantedBookingId,
  releaseWantedBooking,
} from "../features/bookings/bookingsSlice";
import { ArrivyTemplateExtraFields } from "../models/arrivy";
import { selectAddress } from "../features/address/addressSlice";
import { selectCommunityFibre } from "../features/qualification/qualificationSlice";
import {
  fetchDisclaimersAsync,
  selectCheckoutDisclaimer,
} from "../features/disclaimers/disclaimersSlice";
import {
  selectCommunityFibreSurface,
  selectFreeTextDisclaimersEnabled,
  selectPromoPricing,
} from "../features/featureFlags/featureFlagsSlice";
import {
  selectReservation,
  releaseReservation,
} from "../features/reservations/reservationsSlice";
import TimeOutErroModal from "./TimeoutErrorModal";
import { trackPromoCodeValidate } from "../lib/analytics";
import { afterDiscountRate, revertsToRRP } from '../lib/rrp';

export interface DetailProps {
  onSubmit: any;
  previousStep: any;
}

interface CouponForm {
  coupon: string;
  plan: string;
}

interface TermsForm {
  newDevelopment: boolean;
  oneoff: boolean;
  monthly: boolean;
  terms: boolean;
  marketing: boolean;
  routerpayg: boolean;
  router: boolean;
}

const illegalPromoCodes = ["SOMETHING12321"];

const CreditCardInfo = ({ onSubmit, previousStep }: DetailProps) => {
  const cardRef = createRef();
  const dispatch = useAppDispatch();
  const signup = useAppSelector(selectSignUp);
  const booking = useAppSelector(selectWantedBookingId);
  const discount = useAppSelector(selectDiscount);
  const reservation = useAppSelector(selectReservation);

  const [acceptedCoupon, setAcceptedCoupon] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState<boolean>(false);
  const [couponLoading, setCouponLoading] = useState<boolean>(false);
  const [proposedCoupon, setProposedCoupon] = useState<Coupon>({} as Coupon);

  const [showTimeOutErr, setShowTimeOutError] = useState(false);
  const [cardErr, setCardErr] = useState("");
  const [cardLoading, setCarLoading] = useState(false);

  const [termsChecked, setTermsChecked] = useState(false);
  const [marketingChecked, setMarketingChecked] = useState(false);
  const [chargeChecked, setChargeChecked] = useState(false);
  const [routerCheck, setRouterCheck] = useState(false);
  const [routerCheckPayg, setRouterPaygCheck] = useState(false);
  const [newDevelopmentChargeChecked, setNewDevelopmentChargeChecked] = useState(false);

  const ref = useRef<null | HTMLElement>(null);
  const initialCouponValidationTriggered = useRef(false);

  const {
    register, handleSubmit, formState, trigger, getValues,
  } = useForm<CouponForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const termsForm = useForm<TermsForm>({
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const addr = useAppSelector(selectAddress);
  // The qualification carries the building the unit sits in, which sequoia
  // reads out of G-NAF. The order sends it so the CRM keys the building by it.
  const cfQualification = useAppSelector(selectCommunityFibre);
  const cfSurface = useAppSelector(selectCommunityFibreSurface);
  const promoPricing = useAppSelector(selectPromoPricing);
  // With the free text disclaimers, the Checkout Disclaimer replaces the
  // campaign and promo code terms in the agreement lines.
  const freeTextDisclaimersEnabled = useAppSelector(
    selectFreeTextDisclaimersEnabled,
  );
  const checkoutDisclaimer = useAppSelector(selectCheckoutDisclaimer);
  const replacedTerms = checkoutDisclaimer.trim() !== ""
    ? checkoutDisclaimer
    : undefined;
  const discountTerms = freeTextDisclaimersEnabled
    ? replacedTerms
    : discount.terms;

  useEffect(() => {
    if (freeTextDisclaimersEnabled) dispatch(fetchDisclaimersAsync());
  }, [freeTextDisclaimersEnabled]);

  const applyDiscounts = (coupon: Coupon) => {
    dispatch(setCoupon(coupon));
    if (
      coupon.coupon_type === CouponType.ReferralCode
      || coupon.coupon_type === CouponType.SalesCommission
    ) {
      dispatch(setDiscount(newCampaignDiscount("Referral Code Offer")));
    } else {
      dispatch(setDiscount(discountFromCoupon(coupon)));
    }
    if (coupon.coupon_id.includes("2MF") || coupon.coupon_id.includes("OMF")) {
      coupon.coupon_type = CouponType.MoveInCoupon;
      dispatch(setCoupon(coupon));
    }
  };

  const onCouponValidate = (data: CouponForm) => {
    initialCouponValidationTriggered.current = true;
    setCouponLoading(true);
    setAcceptedCoupon(false);
    CheckCoupon(data.coupon, [signup.plan.id])
      .then((resp) => {
        const coupon = resp.data;
        trackPromoCodeValidate(data.coupon, signup.plan, coupon);

        if (
          Object.keys(discount).length !== 0
          && discountWording(discount)
            !== discountWording(discountFromCoupon(coupon))
        ) {
          setShowDiscountModal(true);
          setProposedCoupon(coupon);
          return;
        }

        applyDiscounts(coupon);
        setAcceptedCoupon(true);
      })
      .catch((err) => {
        console.log(err);
        trackPromoCodeValidate(data.coupon, signup.plan);
      })
      .finally(() => {
        setCouponLoading(false);
      });
  };

  // Rejected codes never reach onCouponValidate, the form validation
  // stops them first
  const onCouponInvalid = () => {
    trackPromoCodeValidate(getValues("coupon"), signup.plan);
  };

  const goBack = () => {
    dispatch(releaseWantedBooking());
    dispatch(releaseReservation());
    previousStep();
  };

  const validateCoupon = async (value: string) => {
    setAcceptedCoupon(false);
    ref?.current?.scrollIntoView({
      behavior: "smooth",
    });
    return (
      illegalPromoCodes.indexOf(value) === -1
      && ValidateCoupon(value, addr, signup.plan.id)
    );
  };

  const tokenize = async (termsFormData: any) => {
    setMarketingTerms(termsFormData.marketing);
    setMarketingTerms(termsFormData.terms);
    setChargeChecked(termsFormData.monthly);
    setNewDevelopmentChargeChecked(termsFormData.newDevelopment);
    dispatch(
      setMarketingTerms({
        marketing: termsFormData.marketing ? "YES" : "NO",
        termsandcons: termsFormData.terms ? "YES" : "NO",
      }),
    );

    setCarLoading(true);
    // Use card component ref to call tokenize method
    setCardErr("");
    // @ts-ignore
    await cardRef.current
      .tokenize({})
      .then((data: any) => {
        dispatch(setCbToken({ cb_token: data.vaultToken }));
        const newSignup = { ...signup, cb_token: data.vaultToken };
        newSignup.arrivyExtraFields = {
          ...newSignup.arrivyExtraFields,
          "I Agree to the Terms and Conditions": {
            type: "TEXT",
            name: "I Agree to the Terms and Conditions",
            value: termsFormData.terms ? "YES" : "NO",
          } as ArrivyTemplateExtraFields,
          "Marketing Emails": {
            type: "TEXT",
            name: "Marketing Emails",
            value: termsFormData.marketing ? "YES" : "NO",
          } as ArrivyTemplateExtraFields,
          "Building Code": {
            type: "TEXT",
            name: "Building Code",
            value: addr.uniqueCode,
          } as ArrivyTemplateExtraFields,
        };
        // Community Fibre install the service themselves: there is no
        // booking to make and no installer to send, so the order goes
        // straight to them rather than through the install queue.
        if (isCommunityFibrePlan(signup.plan)) {
          dispatch(
            postOrderAsync({
              signUp: newSignup,
              address: addr,
              contact: {
                firstName: signup.arrivy.customer_first_name,
                lastName: signup.arrivy.customer_last_name,
                email: signup.arrivy.customer_email,
                phone: signup.arrivy.customer_mobile_number,
              },
              preferred: signup.preferredDate ?? "",
              buildingGnafId: cfQualification?.buildingGnafId,
              orderApi: cfSurface,
            }),
          );
        } else {
          dispatch(
            postBookingsAsync({
              payload: { ...signup.arrivy, title: signup.arrivy.title },
              booking,
              signUp: newSignup,
              reservation,
            }),
          );
        }
        // Send ajax calls
        setCarLoading(false);
        onSubmit();
      })
      .catch((err: any) => {
        console.log("ERR", err);
        setCardErr(
          "Credit card is invalid, please check details and try again.",
        );
        setCarLoading(false);
      });
  };

  const getPlanCost = () => {
    const planCost = discount?.period !== 0
      ? getDiscountPlanCost(Number(signup.plan.cost), discount)
      : Number(signup.plan.cost);

    // let cost = signup.router.description2 === 'per month' ? planCost + Number(signup.router.cost) : planCost;
    let cost = planCost;
    cost = signup.addOns.includes("static-ip") ? cost + 9 : cost;

    return cost;
  };

  useEffect(() => {
    ref?.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [cardLoading]);

  useEffect(() => {
    if (
      signup.coupon?.coupon_id
      && !initialCouponValidationTriggered.current
    ) {
      initialCouponValidationTriggered.current = true;
      trigger("coupon");
    }
  }, [signup.coupon?.coupon_id, trigger]);

  return (
    <Container>
      {showDiscountModal && (
        <DiscountModal
          currentDiscount={discount}
          proposedCoupon={proposedCoupon}
          onSubmit={(accepted) => {
            if (accepted) {
              applyDiscounts(proposedCoupon);
              setAcceptedCoupon(true);
            }
            setShowDiscountModal(false);
          }}
        />
      )}
      {showTimeOutErr && <TimeOutErroModal onSubmit={() => goBack()} />}
      <Row className="justify-content-md-center">
        <Col className="d-flex flex-column justify-content-center">
          <h1 className="fw-bold text-primary text-center mb-4">
            Secure Payment
          </h1>
          {reservation && reservation.expires_at && (
            <Alert
              variant="orange"
              className="mx-auto d-inline-block mb-4 my-2 px-2 py-1 text-white bg-orange text-center"
            >
              Hurry! Complete your order before
              {" "}
              <strong>
                <Countdown
                  callBack={setShowTimeOutError}
                  endTime={dayjs(reservation.expires_at)}
                />
              </strong>
              {" "}
              to secure your preferred installation time of
              {" "}
              <strong>{pineAppleDay(new Date(booking.start))}</strong>
              {' '}
              between
              {" "}
              <strong>
                {pineAppleTime(new Date(booking.start), addr.state)}
              </strong>
              {" "}
              -
              {" "}
              <strong>
                {pineAppleTime(new Date(booking.end), addr.state)}
              </strong>
              .
            </Alert>
          )}
        </Col>
      </Row>
      <Row className="position-relative">
        <Col md={8}>
          <CardComponent
            ref={cardRef}
            locale="en"
            className="fieldset field"
            styles={{
              base: {
                fontWeight: "400",
                fontFamily:
                  '"General Sans", "Segoe UI", SegoeUI, "Helvetica Neue", Helvetica, Arial, sans-serif',
                fontSize: "16px",
                color: "#6d6d6d",
                fontSmoothing: "antialiased",
                ":focus": {
                  color: "#6d6d6d",
                },
                "::placeholder": {
                  color: "#fafafa",
                },
                ":focus::placeholder": {
                  color: "#6d6d6d",
                },
              },
            }}
            placeholder={{
              number: "",
              expiry: "MM / YY",
              cvv: "",
            }}
            icon
          >
            <Row>
              <div
                style={{ borderTop: "2px solid" }}
                className="
              align-self-end
              py-2
              border-primary"
              >
                <h4>Payment Details</h4>
              </div>
            </Row>
            <Row className="mb-2">
              <Form.Group
                className="col-xs-4 col-md-12"
                as={Col}
                controlId="creditCardNumber"
              >
                <Form.Label>Card Number</Form.Label>
                <CardNumber className="form-control input-with-radius" />
                <i className="ex1-bar" />
              </Form.Group>
            </Row>
            <Row className="mb-2">
              <Form.Group
                className="col-xs-4 col-md-12"
                as={Col}
                controlId="cardHolderName"
              >
                <Form.Label>Cardholders Name</Form.Label>
                <Form.Control
                  className="form-control input-with-radius"
                  defaultValue={`${signup.arrivy.customer_first_name.toUpperCase()} ${signup.arrivy.customer_last_name.toUpperCase()}`}
                />
              </Form.Group>
            </Row>
            <Row className="mb-2">
              <Form.Group
                className="col-xs-2 col-md-6"
                as={Col}
                controlId="creditCardExpiry"
              >
                <Form.Label>Expiry (MM/YY)</Form.Label>
                <CardExpiry className="form-control input-with-radius" />
                <i className="ex1-bar" />
              </Form.Group>
              <Form.Group
                className="col-xs-2 col-md-6"
                as={Col}
                controlId="creditCardCVV"
              >
                <Form.Label>CVV</Form.Label>
                <CardCVV className="form-control input-with-radius" />
              </Form.Group>
            </Row>

            {/* Always visible — even when the code was prepopulated from the
                URL (referral / move-in links) the customer should see which
                code is applied and be able to change it. */}
            <Stack direction="vertical">
              <h4 className="text-primary">
                Promo / Referral Code (if applicable)
              </h4>

              <Stack direction="horizontal" gap={2}>
                {/* INPUT GROUP */}
                <Form.Group
                  className="col-xs-3 col-md-6 position-relative"
                  as={Col}
                  controlId="coupon"
                >
                  <Form.Control
                    className="input-with-radius"
                    defaultValue={signup.coupon?.coupon_id}
                    style={{
                      borderColor: "#03793E",
                      boxShadow: "0 0 0 0.25rem rgba(3, 121, 62, 0.25)",
                    }}
                    {...register("coupon", {
                      validate: validateCoupon,
                      onChange: () => {
                        setAcceptedCoupon(false);
                      },
                    })}
                  />
                </Form.Group>

                {/* VALIDATE BUTTON */}
                <Button
                  className={
                    signup.coupon && !formState.errors.coupon
                      ? "position-relative items-start"
                      : "position-relative"
                  }
                  variant={formState.errors?.coupon ? "danger" : "primary"}
                  size="lg"
                  onClick={handleSubmit(onCouponValidate, onCouponInvalid)}
                  style={
                    acceptedCoupon
                      ? { background: "transparent", borderWidth: 0 }
                      : formState.errors?.coupon && { color: "white" }
                  }
                >
                  {couponLoading && (
                    <Spinner
                      className="position-absolute"
                      style={{ top: "0.65em", left: "1em" }}
                      animation="grow"
                    />
                  )}

                  {!acceptedCoupon
                    && (formState.errors?.coupon ? "Try again" : "Validate")}
                </Button>
              </Stack>

              {/* ✅ SUCCESS MESSAGE JUST BELOW INPUT — CAN EXPAND FULL WIDTH */}
              {signup?.coupon && acceptedCoupon && (
                <div className="d-flex align-items-start mt-2">
                  <img
                    src="pn-icon-tick.png"
                    alt="success"
                    style={{
                      width: "40px",
                      height: "40px",
                      marginRight: "12px",
                      marginTop: "4px",
                      flexShrink: 0,
                    }}
                  />

                  <div className="fs-5 text-wrap text-primary text-md-nowrap mt-1">
                    Congrats! Please see your order summary to see your discount
                    has successfully been applied.
                  </div>
                </div>
              )}

              {/* ERROR MESSAGE (also not absolute) */}
              {formState.errors?.coupon && (
                <Form.Text className="text-info mt-1">
                  Coupon is invalid
                </Form.Text>
              )}
            </Stack>

            <Row className="mt-2">
              <h4>Your acceptance</h4>
            </Row>
            <Stack gap={2} direction="vertical" className="mb-3">
              <Form.Check
                type="radio"
                id="monthly"
                checked={chargeChecked}
                onClick={() => setChargeChecked(!chargeChecked)}
                label={
                  signup?.coupon?.coupon_id?.includes("3MF") ? (
                    <span>
                      {" I agree to a monthly charge of "}
                      <b>
                        $
                        {getPlanCost()}
                      </b>
                      for the first three months, followed by $
                      {signup.plan.cost}
                      {' '}
                      per month for the next 3 months. After
                      the 6-month promotional period, I agree to be billed
                      monthly at
                      {' '}
                      {afterDiscountRate(signup.plan, promoPricing)}
                      .
                      {" "}
                      {signup.addOns.includes("static-ip")
                        && " (inclusive of monthly Static IP charge)"}
                      {discountTerms !== undefined
                        ? ` ${discountTerms}`
                        : null}
                    </span>
                  ) : (
                    <span>
                      {" I agree to a monthly charge of "}
                      <b>
                        $
                        {getPlanCost()}
                      </b>
                      {signup.addOns.includes("static-ip")
                        && " (inclusive of monthly Static IP charge)"}
                      {
                        " and for this amount to be automatically charged to the payment details provided."
                      }
                      {revertsToRRP(signup.plan, Number(signup.plan.cost), promoPricing)
                        && ` After the promotional period, I agree to be billed monthly at ${afterDiscountRate(signup.plan, promoPricing)}.`}
                      {discountTerms !== undefined
                        ? ` ${discountTerms}`
                        : null}
                    </span>
                  )
                }
                {...termsForm.register("monthly", {
                  required: `Please agree to a monthly charge of $${getPlanCost()}`,
                })}
              />

              {signup?.router?.mode === "Basic Wi-Fi Router PAYG" && (
                <Form.Check
                  type="radio"
                  id="routerpayg"
                  checked={routerCheckPayg}
                  onClick={() => setRouterPaygCheck(!routerCheckPayg)}
                  label={(
                    <span>
                      {" I agree to 12 monthly instalments of "}
                      <b>
                        $
                        {+signup.router.cost}
                      </b>
                      {' '}
                      for the purchase of a
                      router, to be automatically charged to the payment details
                      provided.
                    </span>
                  )}
                />
              )}

              {signup?.router?.mode === "Basic Wi-Fi Router" && (
                <Form.Check
                  type="radio"
                  id="router"
                  checked={routerCheck}
                  onClick={() => setRouterCheck(!routerCheck)}
                  {...termsForm.register("router", {
                    required: "Please agree to the Wi-Fi Router Charge.",
                  })}
                  label={(
                    <span>
                      {" I agree to a one-off payment of "}
                      <b>
                        $
                        {+signup.router.cost}
                      </b>
                      for the purchase of a router and for this amount to be
                      automatically charged to the payment details provided.
                      {discountTerms !== undefined
                        ? ` ${discountTerms}`
                        : null}
                    </span>
                  )}
                />
              )}
              {addr.buildingCategory === "greenfield" && (
                <Form.Check
                  type="radio"
                  id="newDevelopment"
                  checked={newDevelopmentChargeChecked}
                  onClick={() => setNewDevelopmentChargeChecked(!newDevelopmentChargeChecked)}
                  label={(
                    <span>
                      I agree to a one-off New Development Charge of
                      {" "}
                      <strong>$300</strong>
                    </span>
                  )}
                  {...termsForm.register("newDevelopment", {
                    required:
                      "Please agree to the New Development Charge of $300",
                  })}
                />
              )}
              <Form.Check
                type="radio"
                id="terms"
                onClick={() => setTermsChecked(!termsChecked)}
                checked={termsChecked}
                label={(
                  <span>
                    {" I confirm that I have read and agree to "}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://www.pineapple.net.au/wp-content/uploads/PN_General-Terms.pdf"
                    >
                      terms and conditions
                    </a>
                    {
                      " of my plan, and I acknowledge that 30 days’ notice is required should I wish to cancel this service."
                    }
                  </span>
                )}
                {...termsForm.register("terms", {
                  required:
                    "Please confirm that you have read and agree to the terms and conditions of your plan.",
                })}
              />
              <Form.Check
                type="radio"
                id="marketing"
                checked={marketingChecked}
                onClick={() => setMarketingChecked(!marketingChecked)}
                label={(
                  <span>
                    {
                      "I would like to receive marketing and promotional communications from Pineapple Net in accordance with the "
                    }
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://www.pineapple.net.au/wp-content/uploads/PN_Privacy-Policy_12.23.pdf"
                    >
                      Privacy Policy.
                    </a>
                    {" "}
                    Pineapple Net is committed to protecting and respecting your
                    privacy. We will only use your personal information to
                    provide information you have requested, adminster your
                    account, and deliver our products and services to you.
                  </span>
                )}
                {...termsForm.register("marketing", {})}
              />
            </Stack>
            <h4 className="text-center text-info mt-3">{cardErr}</h4>
          </CardComponent>
        </Col>
        <Col>
          <OrderSummary />
        </Col>
      </Row>
      <Row>
        <Col className="text-info">
          {termsForm?.formState?.errors?.newDevelopment
            ? termsForm.formState.errors.newDevelopment.message
            : ""}
        </Col>
      </Row>
      <Row>
        <Col className="text-info">
          {termsForm?.formState?.errors?.monthly
            ? termsForm.formState.errors.monthly.message
            : ""}
        </Col>
      </Row>
      <Row>
        <Col className="text-info">
          {termsForm?.formState?.errors?.router
            ? termsForm.formState.errors.router.message
            : ""}
        </Col>
      </Row>
      <Row>
        <Col className="text-info">
          {termsForm?.formState?.errors?.routerpayg
            ? termsForm.formState.errors.routerpayg.message
            : ""}
        </Col>
      </Row>
      <Row>
        <Col className="text-info">
          {termsForm?.formState?.errors?.terms
            ? termsForm.formState.errors.terms.message
            : ""}
        </Col>
      </Row>
      <Stack
        gap={2}
        className="justify-content-center my-4"
        direction="horizontal"
      >
        <Button
          id="payment_go_back"
          onClick={goBack}
          size="lg"
          variant="outline-primary"
        >
          <span className="px-2">Go back</span>
        </Button>
        <Button
          id="payment_submit"
          onClick={termsForm.handleSubmit(tokenize)}
          size="lg"
          variant="primary"
          disabled={cardLoading}
        >
          <span ref={ref} className="px-2">
            {!cardLoading ? "Submit" : "Please wait..."}
          </span>
        </Button>
      </Stack>
    </Container>
  );
};

export default CreditCardInfo;
